import { useContext, useEffect, useState, useMemo } from "react";
import { toast } from '../../../utils/toast';
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useInventariosApi,
  InventarioItem,
} from "../../../services/api/inventariosApi";
import InventariosModal from "../../../components/module/almacen/InventariosModal/InventariosModal";
import InventariosDataTable from "../../../components/module/almacen/InventariosDataTable/InventariosDataTable";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import InventariosImportModal from "../../../components/module/almacen/InventariosImportModal/InventariosImportModal";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Collapse,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Inventarios = () => {
  const [items, setItems] = useState<InventarioItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventarioItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useInventariosApi();
  const { darkMode } = useContext(DarkModeContext);

  // Computed low-stock items
  const stockBajoItems = useMemo(
    () => items.filter(i => i.stock <= i.StockMinimo),
    [items]
  );

  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 60 },
    {
      field: "articulo",
      headerName: "Artículo",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.articulo?.Nombre || "—",
    },
    {
      field: "sucursal",
      headerName: "Sucursal",
      width: 140,
      valueGetter: (params) => params.row.sucursal?.Nombre || "—",
    },
    {
      field: "stock",
      headerName: "Stock Actual",
      width: 120,
      type: "number",
      renderCell: (params: GridRenderCellParams) => {
        const isBajo = params.row.stock <= params.row.StockMinimo;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isBajo && <WarningAmberIcon sx={{ fontSize: 15, color: "#f44336" }} />}
            <Box sx={{ color: isBajo ? "#f44336" : "inherit", fontWeight: isBajo ? 700 : 400 }}>
              {params.row.stock}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "StockMinimo",
      headerName: "Stock Mín.",
      width: 110,
      type: "number",
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const isBajo = params.row.stock <= params.row.StockMinimo;
        return (
          <Chip
            label={isBajo ? "Stock Bajo" : "Normal"}
            size="small"
            sx={{
              backgroundColor: isBajo
                ? darkMode ? "#ef5350" : "#ffebee"
                : darkMode ? "#66bb6a" : "#e8f5e9",
              color: isBajo
                ? darkMode ? "#fff" : "#c62828"
                : darkMode ? "#fff" : "#2e7d32",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: InventarioItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Eliminar registro?",
      text: "Se eliminará el registro de inventario de esta sucursal.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef5350",
      cancelButtonColor: "#9e9e9e",
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El registro ha sido eliminado.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  return (
    <PageLayout icon={InventoryIcon} title="Inventarios">
        <Box sx={{ padding: "24px", paddingBottom: "48px", minHeight: "calc(100vh - 200px)" }}>

          {/* Alerta de stock bajo */}
          <Collapse in={stockBajoItems.length > 0}>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{ mb: 2, borderRadius: "10px", fontWeight: 500 }}
            >
              {stockBajoItems.length === 1
                ? `1 artículo tiene stock por debajo del mínimo.`
                : `${stockBajoItems.length} artículos tienen stock por debajo del mínimo.`}
              {" "}Revise los registros marcados en rojo.
            </Alert>
          </Collapse>

          <InventariosDataTable
            items={items}
            columns={columns}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAdd={handleAdd}
            onImport={() => setIsImportModalOpen(true)}
            isLoading={isLoading}
            stockBajoCount={stockBajoItems.length}
          />

          <InventariosModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={async (data) => {
              try {
                if (editingItem) {
                  await update(editingItem.Id, data);
                } else {
                  await create(data);
                }
                setIsModalOpen(false);
                await loadItems();
                Swal.fire("Éxito", editingItem ? "Inventario actualizado." : "Inventario creado.", "success");
              } catch (error) {
                console.error("Error saving item:", error);
                toast.error("No se pudo guardar el registro.");
              }
            }}
            title={editingItem ? "Editar Inventario" : "Nuevo Inventario"}
            initialData={
              editingItem
                ? {
                    IdArticulo: editingItem.IdArticulo,
                    IdSucursal: editingItem.IdSucursal,
                    stock: editingItem.stock,
                    StockMinimo: editingItem.StockMinimo,
                  }
                : undefined
            }
            isLoading={isLoading}
          />

          <InventariosImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            onImportComplete={() => { loadItems(); setIsImportModalOpen(false); }}
          />
        </Box>
      </PageLayout>
  );
};

export default Inventarios;
